import { z } from "zod";
import { BackfillBlockedReason, BackfillRequestStatus } from "@carry/shared";
import { PublicIdSchema } from "./common";

/**
 * #769: 過去日の配送実績を後から登録する (後追い登録).
 *
 * 当日の打刻フロー (updateStopStatus) とは**別の操作**として分ける。当日の打刻に混ぜると
 * 「走っていない配送を後から完了にできる」経路が通常導線に紛れるため、過去日専用の経路にし、
 * 保存時点で route_stops を完了にする (#817。運転日報の承認が確認手段)。
 *
 * - ドライバー本人: 過去日報画面から実績を登録する (即反映)
 * - 管理者・事務員: 配車パネルから代理入力する。確認は運転日報の承認で行う
 *
 * 期間のゲートは日数ではなくロック判定 (承認済み運転日報・発行済み請求書) で行う。
 * 判定はすべてサーバ側 (services/delivery-backfill-policy.ts) にあり、UI だけの制限にしない。
 */

export type BackfillRequestStatusDto = z.infer<typeof BackfillRequestStatus>;

export type BackfillBlockedReasonDto = z.infer<typeof BackfillBlockedReason>;

const ActorSchema = z.object({
  id: PublicIdSchema,
  name: z.string(),
});

/**
 * 後追い登録で記録する実績。時刻はシステム時刻で埋めず**実際の時刻を入力させる**
 * (システム時刻だと過去日の便区分・所要時間・日報がすべて壊れるため)。
 * 到着オドメーターは任意 (F29 の委託先支払距離の按分に使う)。
 */
const BackfillRecordFields = {
  actual_arrival: z.iso.datetime({ offset: true }),
  actual_departure: z.iso.datetime({ offset: true }),
  arrival_odometer: z.number().int().nonnegative().nullable().optional(),
  reason: z.string().trim().min(1, "理由を入力してください").max(1000),
};

/** 到着 <= 完了 を課す。ISO 文字列は offset が混ざると辞書順で比較できないので Date で比べる。 */
function arrivalNotAfterDeparture(value: {
  actual_arrival: string;
  actual_departure: string;
}): boolean {
  return Date.parse(value.actual_arrival) <= Date.parse(value.actual_departure);
}

const ARRIVAL_ORDER_MESSAGE = "到着時刻は完了時刻より前にしてください";

export const CreateBackfillRequestInput = z
  .object(BackfillRecordFields)
  .refine(arrivalNotAfterDeparture, {
    message: ARRIVAL_ORDER_MESSAGE,
    path: ["actual_arrival"],
  });
export type CreateBackfillRequestInputDto = z.infer<typeof CreateBackfillRequestInput>;

/** 管理者の代理入力 (申請 + 即承認)。記録する内容はドライバー申請とまったく同じ。 */
export const AdminBackfillStopInput = z
  .object(BackfillRecordFields)
  .refine(arrivalNotAfterDeparture, {
    message: ARRIVAL_ORDER_MESSAGE,
    path: ["actual_arrival"],
  });
export type AdminBackfillStopInputDto = z.infer<typeof AdminBackfillStopInput>;

export const ReviewBackfillRequestInput = z
  .object({
    action: z.enum(["approve", "reject"]),
    review_note: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => v.action !== "reject" || (v.review_note?.trim().length ?? 0) > 0, {
    message: "却下する場合は理由を入力してください",
    path: ["review_note"],
  });
export type ReviewBackfillRequestInputDto = z.infer<typeof ReviewBackfillRequestInput>;

export const ListBackfillRequestsInput = z.object({
  status: BackfillRequestStatus.optional(),
  /** YYYY-MM。未指定は全期間 */
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/, "YYYY-MM 形式で指定してください")
    .optional(),
  // GET の query string は文字列になる。admin 画面は常に limit を送るため coerce 必須。
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListBackfillRequestsInputDto = z.infer<typeof ListBackfillRequestsInput>;

export const BackfillRequest = z.object({
  id: PublicIdSchema,
  stop_id: PublicIdSchema,
  route_id: PublicIdSchema,
  /** 対象の配送日 (レーンの delivery_date)。申請時刻の日付ではない */
  work_date: z.iso.date(),
  status: BackfillRequestStatus,
  actual_arrival: z.iso.datetime({ offset: true }),
  actual_departure: z.iso.datetime({ offset: true }),
  arrival_odometer: z.number().int().nullable(),
  reason: z.string(),
  requested_by: ActorSchema,
  requested_at: z.iso.datetime({ offset: true }),
  reviewed_by: ActorSchema.nullable(),
  reviewed_at: z.iso.datetime({ offset: true }).nullable(),
  review_note: z.string().nullable(),
  /** 一覧で誰のどの配送か分かるようにする表示用の付帯情報 */
  driver_name: z.string().nullable(),
  stop_label: z.string().nullable(),
});
export type BackfillRequestDto = z.infer<typeof BackfillRequest>;

/**
 * 後追い登録が可能かのサーバ判定。driver / admin いずれの画面も、この結果だけを見て
 * 導線を出すか決める (UI に判定ロジックを持たせない)。
 */
export const BackfillEligibility = z.object({
  stop_id: PublicIdSchema,
  work_date: z.iso.date(),
  /** レーンの担当ドライバー。未割当は null。代理入力後の運転日報リンクに使う */
  driver_public_id: PublicIdSchema.nullable(),
  allowed: z.boolean(),
  blocked_reason: BackfillBlockedReason.nullable(),
  /**
   * この stop の直近の申請。無ければ null。
   * `status='pending'` なら二重申請の抑止に使い、`rejected` なら却下されたことと理由を
   * 申請者へ返すのに使う (却下が本人に伝わらないと再申請の判断ができない)。
   */
  latest_request: z
    .object({
      id: PublicIdSchema,
      status: BackfillRequestStatus,
      review_note: z.string().nullable(),
      reviewed_at: z.iso.datetime({ offset: true }).nullable(),
    })
    .nullable(),
});
export type BackfillEligibilityDto = z.infer<typeof BackfillEligibility>;
